import { Injectable } from '@angular/core';

import { LayerApi } from '../api/layer-api';
import { ESRI } from '../esri-loader-util';
import { IClassBreakData, IMapcoreMapImageLayer, IQuery, isClassBreaksRenderer, ISplitThemeConfiguration, isSimpleRenderer, isSymbolWithStyle, isUniqueValueRenderer, IThemeType, MapCoreLayer, QueryableMapCoreLayer } from '../types';

import { LegendService } from './legend.service';

@Injectable({
  providedIn: 'root'
})
export class SplitThemeService {

  mapView: __esri.MapView;

  minMaxes = {
    'unique-value': {
        min: 2,
        max: 100
    },
    'class-break': {
        min: 3,
        max: 25
    }
  };

  constructor(private readonly layerApi: LayerApi,
              private readonly legendService: LegendService) {
  }

  getLayerColumnsFromLayer(layer: MapCoreLayer) {
      let retVal: { name: string; alias: string }[];
      if (layer && layer.mapcoreProps && layer.mapcoreProps.columns && layer.mapcoreProps.columns.length) {
          retVal = layer.mapcoreProps.columns
              .map(c => ({ name: c.name, alias: c.alias }))
              .sort(function(a, b) {
                  return a.alias.localeCompare(b.alias, 'en', { sensitivity: 'base', numeric: true });
              });
      }
      return retVal;
  }

  async getLayerClassBreakData(layerId: number, fieldName: string, numSelectedClasses: number, forceUniqueValues: boolean, query: IQuery, columnIsDate: boolean = false, classBreakLabels: string = '[]') {
      let retVal: IClassBreakData;
      const results = await this.layerApi.getClassBreakData(layerId, [fieldName], numSelectedClasses, forceUniqueValues, query, columnIsDate, classBreakLabels);
      if (results && results.length) {
          retVal = results[0].classBreakData;
      }
      return retVal;
  }

  public async getSimpleLayerSymbols(layer: IMapcoreMapImageLayer): Promise<__esri.Symbol[]> {
      const symbols: __esri.Symbol[] = [];
      for (let i = 0; i < layer.sublayers.length; i++) {
          symbols.push(await this.getSimpleSublayerSymbol(layer, i));
      }
      return symbols;
  }

  private async getSimpleSublayerSymbol(layer: IMapcoreMapImageLayer, index: number): Promise<__esri.Symbol> {
      let symbol: __esri.Symbol;
      const r = layer.sublayers.getItemAt(index).renderer;
      if (isSimpleRenderer(r)) {
          symbol = r.symbol;
      } else if (isClassBreaksRenderer(r)) {
          symbol = r.classBreakInfos[0].symbol;
      } else if (isUniqueValueRenderer(r)) {
          symbol = r.uniqueValueInfos[0].symbol;
      } else if (layer.mapcoreProps.simpleRenderers) {
          symbol = layer.mapcoreProps.simpleRenderers[index].symbol;
      }
      return symbol;
  }

  async getClassColors(count: number, currentColors: __esri.Color[], symbol?: __esri.Symbol, useSymbolScheme: boolean = false, useEsriScheme: boolean = false): Promise<[__esri.Color[], __esri.ColorSchemes]> {
      let colors: __esri.Color[] = [];
      const [Color, SymbologyColor] = await Promise.all([ESRI.Color, ESRI.SymbologyColor]);
      const scheme = SymbologyColor.getSchemes({ geometryType: 'polyline', basemap: this.mapView.map.basemap, theme: 'high-to-low' });
      const tenBase = 10;
      const fiveBase = 5;
      const defaultFillOpacity = 0.5;
      if (symbol && (symbol.type === 'simple-fill' || useSymbolScheme) && (!isSymbolWithStyle(symbol) || (isSymbolWithStyle(symbol) && symbol.style !== 'none')) && this.getOpacity(symbol.color) !== 0) {
          // for non-transparent symbols, always use symbol based color gradient for polygons and, if requested, for points/lines
          // if new colors are needed, recalculate colors to make best use of the available color range.
          if (currentColors && currentColors.length && currentColors.length === count) {
              colors = currentColors;
          } else {
              colors = await this.getColorGradient(symbol.color, count);
          }
      } else if (useEsriScheme) {
          // use ESRI default scheme colors keeping current theme colors if present
          const esriColors: __esri.Color[] = [];
          scheme.primaryScheme.colorsForClassBreaks.filter(x => x.numClasses === tenBase)[0].colors.forEach(breakColor => {
              esriColors.push(breakColor);
          });
          scheme.secondarySchemes[0].colorsForClassBreaks.filter(x => x.numClasses === tenBase)[0].colors.forEach(breakColor => {
              esriColors.push(breakColor);
          });
          scheme.secondarySchemes[1].colorsForClassBreaks.filter(x => x.numClasses === fiveBase)[0].colors.forEach(breakColor => {
              esriColors.push(breakColor);
          });
          if (symbol.type === 'simple-fill') {
              esriColors.forEach(c => c.a = defaultFillOpacity);
          }
          for (let i = 0; i < this.minMaxes['unique-value'].max; i++) {
              colors.push(currentColors && currentColors.length > i ? currentColors[i] : esriColors[i]);
          }
      } else {
          // use MapCore default layer colors. If any colors were customized, they will be re-inserted into the color set
          // before assigning them to classes.
          const defaultColors = this.legendService.getDefaultColors();

          for (let i = 0; i < this.minMaxes['unique-value'].max; i++) {
              if (defaultColors.length > i) {
                  colors.push(Color.fromArray(defaultColors[i]));
              } else {
                  colors.push(Color.fromArray(this.legendService.getRandomColorRGB()));
              }
          }

          if (symbol.type === 'simple-fill') {
              colors.forEach(c => c.a = defaultFillOpacity);
          }
      }
      return [colors, scheme];
  }

  private async getColorGradient(baseColor: __esri.Color | number[], length): Promise<__esri.Color[]> {
    const Color = await ESRI.Color;
    const minOpacity = 0.05;
    const color = Array.isArray(baseColor) ? baseColor : baseColor.toRgba();
    const direction = Math.max(color[0], color[1], color[2]) > 127 ? -1 : 1;
    const avoidTooDarkOrLight = 1.5;
    const deltas = color.map(h => {
        const startPoint = direction === 1 ? 255 - h : h;
        return Math.floor(startPoint / (length * avoidTooDarkOrLight) * direction);
    });
    const colors = Array.from({ length }, (_, i) => {
        return new Color([
            color[0] + deltas[0] * i,
            color[1] + deltas[1] * i,
            color[2] + deltas[2] * i,
            Math.max(color[3], minOpacity)
        ]);
    });
    return direction === 1 ? colors.reverse() : colors;
  }

  private getOpacity(color: __esri.Color): number {
      return Array.isArray(color) ? color[3] : color.a;
  }

  validateClassBreakCount(rendererType: IThemeType, classBreaks: number) {
    const classBreakLimits = this.minMaxes[rendererType];

    if (classBreaks < classBreakLimits.min) {
      return `This field has fewer than ${classBreakLimits.min} class breaks. Please try changing the layer filter or selecting a different field.`;
    } else if (classBreaks > classBreakLimits.max) {
      return `This field has more than ${classBreakLimits.max} class breaks. Please try filtering the layer or selecting a different field.`;
    }
  }

  /**
   * Strips layer references and column metadata from the theme for saving.
   * @param theme The theme to modify.
   * @returns A stripped copy of the theme.
   */
  getThemeForSave(theme: ISplitThemeConfiguration): ISplitThemeConfiguration {
    return {
        ...theme,
        layerColumns: null,
        groupLayer: null,
        classBreaks: theme.classBreaks.map(cb => ({ ...cb, layer: null }))
    };
  }

  /**
   * Adds data back into the theme that was stripped before saving.
   * @param groupLayer The group layer containing the theme.
   */
  async rehydrateTheme(groupLayer) {
    const JsonUtils = await ESRI.JsonUtils;
    const theme = groupLayer.mapcoreProps.splitTheme;

    theme.groupLayer = groupLayer;
    theme.layerColumns = this.getLayerColumnsFromLayer(groupLayer.layers.getItemAt(0));
    theme.baseSymbols = theme.baseSymbols.map(bs => JsonUtils.fromJSON(bs));

    for (const classBreak of theme.classBreaks) {
      classBreak.layer = groupLayer.layers.find(l => l.mapcoreProps.splitThemeLabel === classBreak.info.label) as QueryableMapCoreLayer;
      classBreak.symbols = classBreak.symbols.map(sym => JsonUtils.fromJSON(sym));
    }
  }
}
